/**
 * What a document is paid up for, reduced to what the picker and the spine need.
 *
 * `getDocumentPayment` (`GET /billing/documents/{doc}/payment`) answers with a
 * whole-document flag and a list of covered root section numbers. Everywhere in
 * the add-in that decides "is this section locked" or "which roots still cost
 * money" wants those two facts as a set, so the reduction lives here, pure and
 * testable, rather than being re-derived at each call site.
 */

import type { DocumentPayment } from "../../api/client";

export interface Coverage {
  /** A whole-document (or custom) purchase covers every section. */
  readonly whole: boolean;
  /** Root section numbers ("9") covered individually. */
  readonly roots: ReadonlySet<string>;
}

export const NO_COVERAGE: Coverage = { whole: false, roots: new Set() };

/** Reduce a `DocumentPayment` to the coverage the UI reasons over. */
export function coverageOf(payment: DocumentPayment | null | undefined): Coverage {
  if (!payment) return NO_COVERAGE;
  const whole = payment.covered_whole === true || payment.whole_document === true;
  const roots = new Set<string>([...(payment.sections_covered ?? []), ...(payment.sections_paid ?? [])]);
  return { whole, roots };
}

/**
 * Which of a document's root section numbers are still locked (unpaid).
 *
 * A whole-document purchase locks nothing. Otherwise a root is locked when it is
 * not in the covered set. `allRoots` is the doctype's full list of billable
 * roots, in document order.
 */
export function lockedRoots(allRoots: readonly string[], coverage: Coverage): ReadonlySet<string> {
  if (coverage.whole) return new Set();
  return new Set(allRoots.filter((root) => !coverage.roots.has(root)));
}
